import { useMutation } from '@common/hooks';
import { Form, Select } from 'antd';
import { Store } from 'antd/lib/form/interface';
import React from 'react';
import { Modal } from '@common/components';
import { createMessage } from 'works/legacy/lib/message-helper';
import {
  ExportLineItemsArgs,
  ExportLineItemsFileResponse,
  WorksInvoiceExportMutation,
  WorksReverseInvoiceExportMutation,
} from '@pay-bill/graphql';

const LABEL_TYPE = {
  invoice: 'Invoice',
  reverse_invoice: 'Reverse invoice',
};

const MUTATIONS = {
  general_invoice: WorksInvoiceExportMutation,
  general_reverse_invoice: WorksReverseInvoiceExportMutation,
};

const FILE_TYPE_OPTIONS = [
  { value: 'csv', label: 'Comma-separated Values (.csv)' },
  { value: 'xlsx', label: 'Microsoft Excel (.xlsx)' },
  { value: 'tsv', label: 'Tab-separated Values (.tsv)' },
];

export const HoursExportModal = ({
  visible,
  onAction,
  id,
  entityType,
}: {
  visible: boolean;
  onAction: () => void;
  id: string;
  entityType: 'invoice' | 'reverse_invoice';
}) => {
  const [form] = Form.useForm();

  const mutationKey: keyof typeof MUTATIONS = `general_${entityType}`;

  const createSuccessMessage = () => {
    createMessage(
      'success',
      `Your ${LABEL_TYPE[entityType]} Export is being generated. You'll receive an email with the final file when complete. Large exports may take a few minutes.`,
      10
    );
  };

  const createErrorMessage = () => {
    createMessage(
      'error',
      `${LABEL_TYPE[entityType]} Export failed. Please try again.`
    );
  };

  const [performExport, { loading }] = useMutation<
    ExportLineItemsFileResponse,
    ExportLineItemsArgs
  >(MUTATIONS[mutationKey], {
    onCompleted: (response) => {
      if (response.result.success) {
        createSuccessMessage();
        onAction();
      } else {
        createErrorMessage();
      }
    },
    onError: createErrorMessage,
  });

  const exportFile = (values: Store) => {
    performExport({
      variables: {
        id: id,
        format: values.fileType,
      },
    });
  };

  return (
    <Modal
      visible={visible}
      cancelText="Cancel"
      okText="Download"
      onCancel={onAction}
      title="Hours"
      confirmLoading={loading}
      onOk={form.submit}
      afterClose={() => form.resetFields()}
    >
      <Form layout="vertical" form={form} onFinish={exportFile}>
        <Form.Item
          name="fileType"
          label="File Type"
          rules={[{ required: true, message: 'Please select the file type' }]}
        >
          <Select
            placeholder="Select one"
            size="large"
            options={FILE_TYPE_OPTIONS}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
